import React, { useEffect } from 'react';
import { useNavigate, useParams, Link, useLocation, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Calculator,
  Ruler,
  Scale,
  ArrowRight,
  Thermometer,
  Clock,
  Code,
  Utensils,
  Database,
  Zap,
  Gauge,
  Car,
  Compass,
  Radio,
  Fuel,
  Type,
  Camera,
  Wrench,
  Activity,
  Droplet,
  Wind,
  Volume2,
  HelpCircle,
  X,
  Shirt,
  MapPin,
  Droplets,
  Magnet,
  Trees,
  Flower2,
  Beer,
  Anchor,
  Music,
  Dumbbell,
  Palette,
  Gamepad2,
  Mountain,
  FlaskConical,
  Cloud,
  Building2,
  Printer,
  CircuitBoard,
  Atom,
  Eye,
  Speaker,
  Globe,
  Brain
} from 'lucide-react';
import { ConversionForm } from './components/ConversionForm';
import { SmartCalculator } from './components/SmartCalculator';
import { UnitComparison } from './components/UnitComparison';
import { AdvancedTools } from './components/AdvancedTools';
import { ToolsPanel } from './components/ToolsPanel';
import { UnitCategory } from './types/units';
import { unitsByCategory, getUnitByValue } from './utils/units';
import { MainContent } from './components/MainContent';
import { BlogPost } from './components/BlogPost';
import { BlogList } from './components/BlogList';
import { popularConversions } from './data/popularConversions';

function App() {
  const { category = 'length' } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [showTutorial, setShowTutorial] = React.useState(true);
  const conversions = popularConversions[category as UnitCategory] || [];

  const handleCategoryChange = (newCategory: UnitCategory) => {
    navigate(`/${newCategory}`);
  };

  const categoryDescription = getCategoryDescription(category);

  // Set page title and meta description for SEO
  useEffect(() => {
    window.scrollTo(0, 0);

    // Parse conversion path if present
    if (category) {
      const [fromUnit, toUnit] = category.split('-to-');
      if (fromUnit && toUnit) {
        // Update form with conversion values
        // You can implement this by passing state up from ConversionForm
      }
    }
  }, [category]);

  useEffect(() => {
    // Send pageview to Google Analytics
    window.gtag('send', 'pageview', location.pathname + location.search);
  }, [location]);

  const categoryLabel = getUnitByValue(category, unitsByCategory[category]?.[0]?.value)?.label || '';
  const units = unitsByCategory[category]?.map(u => u.label).join(', ') || '';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>Unit Converter - {category.charAt(0).toUpperCase() + category.slice(1)}</title>
        <meta name="description" content={`Free online ${categoryLabel.toLowerCase()} converter. Convert between ${units}. Fast, accurate conversions with step-by-step calculations.`} />
        <meta name="keywords" content={`${categoryLabel.toLowerCase()} converter, unit converter, ${units.toLowerCase()}, conversion calculator`} />
        <link rel="canonical" href={`https://easyunitconverter.com/${category}`} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content={`Convert ${categoryLabel} Units - Free Online Unit Converter`} />
        <meta property="og:description" content={`Free online ${categoryLabel.toLowerCase()} converter. Convert between ${units}. Fast, accurate conversions with step-by-step calculations.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://easyunitconverter.com/${category}`} />
        <meta property="og:image" content="https://easyunitconverter.com/og-image.jpg" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Convert ${categoryLabel} Units - Free Online Unit Converter`} />
        <meta name="twitter:description" content={`Free online ${categoryLabel.toLowerCase()} converter. Convert between ${units}. Fast, accurate conversions with step-by-step calculations.`} />
        <meta name="twitter:image" content="https://easyunitconverter.com/og-image.jpg" />
        
        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": `${categoryLabel} Unit Converter`,
            "description": `Free online ${categoryLabel.toLowerCase()} converter. Convert between ${units}.`,
            "url": `https://unitconverterplus.com/${category}`,
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          })}
        </script>
      </Helmet>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Tutorial */}
        {showTutorial && (
          <div className="mb-8 relative">
            <button
              onClick={() => setShowTutorial(false)}
              className="absolute top-2 right-2 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close tutorial"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
            <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-blue-50 rounded-xl border border-blue-100">
              <HelpCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
              <div className="space-y-3">
                <h2 className="font-semibold text-gray-900">Welcome to Unit Converter Plus!</h2>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>Here's how to get started:</p>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Choose a category from the grid above</li>
                    <li>Enter a value and select your units</li>
                    <li>Get instant conversions with step-by-step calculations</li>
                  </ol>
                  <p className="text-sm text-gray-500 mt-2">
                    <strong>Pro tip:</strong> Use the Smart Calculator for complex calculations and the Advanced Tools for specialized conversions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Category Title and Description */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {categoryDescription.title}
          </h1>
          <p className="text-gray-600">
            {categoryDescription.description}
          </p>
        </div>

        {/* Category Grid */}
        <div className="max-h-[calc(100vh-16rem)] overflow-y-auto rounded-lg border border-gray-100 bg-white/80 backdrop-blur p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 sm:gap-3">
            {Object.keys(unitsByCategory).map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat as UnitCategory)}
                className={`p-3 sm:p-4 rounded-lg flex items-center gap-2 text-sm sm:text-base ${
                  category === cat 
                    ? 'bg-blue-50/70 text-blue-600 ring-2 ring-blue-500/20 shadow-sm' 
                    : 'bg-gray-50/50 hover:bg-gray-50/80 hover:shadow-sm transition-all'
                }`}
              >
                {getCategoryIcon(cat)}
                <span>{cat.split('_').map(word => 
                  word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ')}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Routes */}
        <Routes>
          <Route path="/blog/:blogId" element={<BlogPost />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/:category" element={<MainContent />} />
          <Route path="/" element={<MainContent />} />
        </Routes>

        {conversions.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Popular Conversions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {conversions.map((conv) => (
                <Link
                  key={conv.url}
                  to={`/${category}/${conv.url}`}
                  className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow"
                >
                  {conv.from} to {conv.to}
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function getCategoryIcon(category: string) {
  const iconProps = { className: "w-5 h-5" };
  switch (category) {
    case 'length': return <Ruler {...iconProps} />;
    case 'weight': return <Scale {...iconProps} />;
    case 'temperature': return <Thermometer {...iconProps} />;
    case 'cooking': return <Utensils {...iconProps} />;
    case 'dev': return <Code {...iconProps} />;
    case 'time': return <Clock {...iconProps} />;
    case 'data': return <Database {...iconProps} />;
    case 'energy': return <Zap {...iconProps} />;
    case 'pressure': return <Gauge {...iconProps} />;
    case 'speed': return <Car {...iconProps} />;
    case 'angle': return <Compass {...iconProps} />;
    case 'frequency': return <Radio {...iconProps} />;
    case 'fuel': return <Fuel {...iconProps} />;
    case 'typography': return <Type {...iconProps} />;
    case 'photography': return <Camera {...iconProps} />;
    case 'torque': return <Wrench {...iconProps} />;
    case 'radiation': return <Activity {...iconProps} />;
    case 'density': return <Droplet {...iconProps} />;
    case 'flow': return <Wind {...iconProps} />;
    case 'sound': return <Volume2 {...iconProps} />;
    case 'textile': return <Shirt {...iconProps} />;
    case 'astronomy': return <Globe {...iconProps} />;
    case 'surveying': return <MapPin {...iconProps} />;
    case 'viscosity': return <Droplets {...iconProps} />;
    case 'magnetic': return <Magnet {...iconProps} />;
    case 'lumber': return <Trees {...iconProps} />;
    case 'gardening': return <Flower2 {...iconProps} />;
    case 'brewing': return <Beer {...iconProps} />;
    case 'maritime': return <Anchor {...iconProps} />;
    case 'electronics': return <CircuitBoard {...iconProps} />;
    case 'nuclear': return <Atom {...iconProps} />;
    case 'quantum': return <Atom {...iconProps} />;
    case 'optics': return <Eye {...iconProps} />;
    case 'acoustics': return <Speaker {...iconProps} />;
    case 'magnetism': return <Magnet {...iconProps} />;
    case 'cosmology': return <Globe {...iconProps} />;
    case 'biology': return <FlaskConical {...iconProps} />;
    case 'networking': return <Globe {...iconProps} />;
    case 'digital_storage': return <Database {...iconProps} />;
    case 'computer_memory': return <Database {...iconProps} />;
    case 'ai-ml': return <Brain {...iconProps} />;
    default: return <Ruler {...iconProps} />;
  }
}

function getCategoryDescription(category: string): { title: string; description: string } {
  // Helper function to properly capitalize category names
  const capitalizeCategory = (cat: string) => {
    // Handle special cases
    if (cat === 'dev') return 'Developer';
    if (cat === 'faq') return 'FAQ';
    
    // Split by underscore and capitalize each word
    return cat.split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const descriptions: Record<string, { title: string; description: string }> = {
    length: {
      title: "Length & Distance Converter",
      description: "Convert between metric and imperial units of length, from microscopic measurements to astronomical distances."
    },
    weight: {
      title: "Weight & Mass Converter",
      description: "Transform between different weight units, perfect for cooking, shipping, or scientific calculations."
    },
    temperature: {
      title: "Temperature Converter",
      description: "Switch between Celsius, Fahrenheit, and Kelvin with precise calculations and formulas."
    },
    cooking: {
      title: "Cooking Units Converter",
      description: "Convert between volume measurements used in recipes from different regions and standards."
    },
    dev: {
      title: "Developer Units Converter",
      description: "Transform between various web development units like pixels, rem, em, and viewport units."
    },
    time: {
      title: "Time Converter",
      description: "Convert between different time units, from milliseconds to years."
    },
    data: {
      title: "Data Storage Converter",
      description: "Transform between digital storage units, from bits to petabytes."
    },
    energy: {
      title: "Energy Units Converter",
      description: "Convert between various energy measurements used in physics and engineering."
    }
  };

  return descriptions[category] || {
    title: `${capitalizeCategory(category)} Converter`,
    description: `Convert between different ${category} units with precise calculations and formulas.`
  };
}

export default App;